import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import vsImg from "../assets/vs.png";
import betIcon from "../assets/bet.png";
import chartIcon from "../assets/chart.png";
import fire_1 from "../assets/gif/fire_1.gif";
import fire_2 from "../assets/gif/fire_2.gif";
import fire_3 from "../assets/gif/fire_3.gif";
import electric_1 from "../assets/gif/electric_1.gif";
import electric_2 from "../assets/gif/electric_2.gif";
import electric_3 from "../assets/gif/electric_3.gif";
import lineLeft from "../assets/line_left.png";
import lineRight from "../assets/line_right.png";
import lineRightCompleted from "../assets/line_right_c.png";
import positivePnlBg from "../assets/positive_pnl.png";
import negativePnlBg from "../assets/negative_pnl.png";
import victoryImg from "../assets/victory.png";
import defeatImg from "../assets/defeat.png";
import chartIconSvg from "../assets/icons/chart_icon.svg";
import apiService from "../services/apiService";
import marketsData from "../utils/markets.json";
import { useDuelWebSocket } from "../hooks/useDuelWebSocket";
import { parseHttpDate } from "../utils/helper";
import "./DuelLive.css";

// Normalize the DB duel record into a flat, UI-friendly shape
function normalizeDuel(raw) {
  if (!raw) return null;

  const getNum = (v) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === "number") return v;
    if (typeof v === "string") return Number(v) || 0;
    return 0;
  };

  const getOptionalNum = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") return v;
    if (typeof v === "string") return Number(v) || null;
    return null;
  };

  const result = {
    id: String(raw._id || ""),
    challengerUserId: getNum(raw.challenger_user_id),
    opponentUserId: getNum(raw.opponent_user_id),
    challengerUsername: raw.challenger_username?.toUpperCase() || "CHALLENGER",
    challengerAvatarUrl: raw.challenger_avatar_url || null,
    challengerLighterAccountIndex: getNum(raw.challenger_lighter_account_index),
    challengerDuelWins: getNum(raw.challenger_duel_wins),
    challengerDuelLosses: getNum(raw.challenger_duel_losses),
    challengerWinCount: getNum(raw.challenger_win_count),
    opponentUsername: raw.opponent_username?.toUpperCase() || "HOST",
    opponentAvatarUrl: raw.opponent_avatar_url || null,
    opponentLighterAccountIndex: getNum(raw.opponent_lighter_account_index),
    opponentDuelWins: getNum(raw.opponent_duel_wins),
    opponentDuelLosses: getNum(raw.opponent_duel_losses),
    opponentWinCount: getNum(raw.opponent_win_count),
    winnerUserId: getOptionalNum(raw.winner_user_id),
    durationMinutes: getNum(raw.duration_minutes) || 5,
    status: raw.status || "active",
    challengerPnl: getNum(raw.challenger_pnl) || 0,
    opponentPnl: getNum(raw.opponent_pnl) || 0,
    prizeXp: getNum(raw.prize_xp_amount) || 0,
    wagerXp: getOptionalNum(raw.wager_xp_amount),
    rounds: getNum(raw.rounds) || 0,
    odds: raw.odds
      ? {
          oddsA:
            typeof raw.odds.odds_A === "number" && !isNaN(raw.odds.odds_A)
              ? raw.odds.odds_A
              : null,
          oddsB:
            typeof raw.odds.odds_B === "number" && !isNaN(raw.odds.odds_B)
              ? raw.odds.odds_B
              : null,
          probA:
            typeof raw.odds.prob_A === "number" && !isNaN(raw.odds.prob_A)
              ? raw.odds.prob_A
              : null,
          probB:
            typeof raw.odds.prob_B === "number" && !isNaN(raw.odds.prob_B)
              ? raw.odds.prob_B
              : null,
        }
      : null,
    createdAt: parseHttpDate(raw.created_at) || Date.now() - 3 * 60 * 1000,
    updatedAt: parseHttpDate(raw.updated_at) || Date.now(),
  };

  return result;
}

function PnlRibbon({ value, side = "left", style = {}, spanStyle = {} }) {
  const sign = value >= 0 ? "+" : "";
  const cls = value >= 0 ? "pnl-ribbon up" : "pnl-ribbon down";
  const sideCls = side === "left" ? "left" : "right";
  const bgImage = value >= 0 ? positivePnlBg : negativePnlBg;

  return (
    <div className={`${cls} ${sideCls}`} style={style}>
      <span style={spanStyle}>
        PNL {sign}
        {value.toFixed(2)}%
      </span>
      <img src={bgImage} alt="PNL Background" />
    </div>
  );
}

export default function DuelLive() {
  const { duelId } = useParams();
  const navigate = useNavigate();
  const [duel, setDuel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trades, setTrades] = useState([]);
  const [shouldRefetchDuel, setShouldRefetchDuel] = useState(false);
  const [lastRefetchTime, setLastRefetchTime] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showStakePoolModal, setShowStakePoolModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'completed' or 'not-found'
  const [showSpell, setShowSpell] = useState(false);
  const [spellType, setSpellType] = useState(null); // 'fireball' or 'fire_1'
  const [showBetModal, setShowBetModal] = useState(false);
  const [selectedTrader, setSelectedTrader] = useState(null); // 'challenger' or 'opponent'
  const [betAmount, setBetAmount] = useState("");
  const [betLoading, setBetLoading] = useState(false);
  const [showBetResultModal, setShowBetResultModal] = useState(false);
  const [betResult, setBetResult] = useState(null); // { success: boolean, message: string }
  const [marketSymbolMap, setMarketSymbolMap] = useState({});
  const [bets, setBets] = useState([]);

  // Initialize market symbol mapping on component mount
  useEffect(() => {
    const loadMarketData = async () => {
      try {
        // First try to fetch from GCS URL
        console.log("Attempting to fetch markets from GCS...");
        const response = await fetch(
          "https://storage.googleapis.com/lumos-cdn/files/markets.json"
        );
        const markets = await response.json();
        console.log("Successfully loaded markets from GCS");

        // Create a symbol mapping for quick lookup
        const symbolMap = {};
        markets.forEach((market) => {
          symbolMap[market.market_id] = market.symbol;
        });
        setMarketSymbolMap(symbolMap);
      } catch (error) {
        console.warn(
          "Failed to fetch markets from GCS, falling back to local file:",
          error.message
        );

        // Fallback to local markets data
        console.log("Loading markets from local file...");
        const symbolMap = {};
        marketsData.forEach((market) => {
          symbolMap[market.market_id] = market.symbol;
        });
        setMarketSymbolMap(symbolMap);
      }
    };

    loadMarketData();
  }, []);

  useEffect(() => {
    const fetchDuel = async () => {
      if (!duelId || duel) {
        setLoading(false);
        return;
      }
      console.log('fetching duel', duelId);

      try {
        setLoading(true);
        const duelData = await apiService.getDuel(duelId);
        const parsedDuelData =
          typeof duelData === "string" ? JSON.parse(duelData) : duelData;
        const normalizedDuel = normalizeDuel(parsedDuelData);
        // console.log('normalizedDuel', normalizedDuel);

        setDuel(normalizedDuel);
        setError(null);
      } catch (err) {
        console.error("Error fetching duel:", err);
        setError("Failed to load duel details");
      } finally {
        setLoading(false);
      }
    };

    fetchDuel();
  }, [duelId, duel]);

  // Handle duel refetch when shouldRefetchDuel is true
  useEffect(() => {
    if (!shouldRefetchDuel || !duelId) return;

    const now = Date.now();
    // Throttle refetch to max once per 5 seconds to avoid excessive API calls
    if (now - lastRefetchTime < 5000) {
      console.log("Refetch throttled, skipping");
      setShouldRefetchDuel(false);
      return;
    }

    const refetchDuel = async () => {
      try {
        console.log("Refetching duel data due to account updates");
        const duelData = await apiService.getDuel(duelId);
        const parsedDuelData =
          typeof duelData === "string" ? JSON.parse(duelData) : duelData;
        const normalizedDuel = normalizeDuel(parsedDuelData);
        setDuel(normalizedDuel);
        setLastRefetchTime(now);
        console.log("Duel data refetched successfully");
      } catch (err) {
        console.error("Error refetching duel:", err);
      } finally {
        setShouldRefetchDuel(false);
      }
    };

    refetchDuel();
  }, [shouldRefetchDuel, duelId, lastRefetchTime]);

  // TODO: Handle bets refetch when shouldRefetchBets is true
  // useEffect(() => {
  //   if (!shouldRefetchBets || !duelId) return;

  //   const now = Date.now();
  //   // Throttle refetch to max once per 5 seconds to avoid excessive API calls
  //   if (now - lastBetsRefetchTime < 5000) {
  //     console.log("Bets refetch throttled, skipping");
  //     setShouldRefetchBets(false);
  //     return;
  //   }

  //   const refetchBets = async () => {
  //     try {
  //       console.log("Refetching bets data due to account updates");
  //       const betsData = await apiService.getDuelBets(duelId);
  //       const parsedBetsData =
  //         typeof betsData === "string" ? JSON.parse(betsData) : betsData;
  //       setBets(Array.isArray(parsedBetsData) ? parsedBetsData : []);
  //       setLastBetsRefetchTime(now);
  //       console.log("Bets data refetched successfully");
  //     } catch (err) {
  //       console.error("Error refetching bets:", err);
  //     } finally {
  //       setShouldRefetchBets(false);
  //     }
  //   };

  //   refetchBets();
  // }, [shouldRefetchBets, duelId, lastBetsRefetchTime]);

  // Periodic duel fetching every 10 seconds when status !== 'completed'
  useEffect(() => {
    if (!duelId || !duel || duel.status === "completed") {
      return;
    }

    const interval = setInterval(async () => {
      try {
        console.log("Periodic duel fetch every 10s");
        const duelData = await apiService.getDuel(duelId);
        const parsedDuelData =
          typeof duelData === "string" ? JSON.parse(duelData) : duelData;
        const normalizedDuel = normalizeDuel(parsedDuelData);
        setDuel(normalizedDuel);
      } catch (err) {
        console.error("Error in periodic duel fetch:", err);
      }
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [duelId, duel]);

  // Handle spell animation and trade updates from WebSocket
  const handleTradesUpdate = (updatedTrades, accountInfo) => {
    setTrades(updatedTrades);

    // Determine spell type based on which account triggered the trade
    if (accountInfo) {
      const isChallengerTrade = accountInfo.accountType === 'challenger';
      setSpellType(
        isChallengerTrade
          ? ["fire_1", "fire_2", "fire_3"][Math.floor(Math.random() * 3)]
          : ["electric_1", "electric_2", "electric_3"][Math.floor(Math.random() * 3)]
      );
      setShowSpell(true);
    }
  };

  // Use the WebSocket hook for duel data
  useDuelWebSocket(duel, handleTradesUpdate);

  // Initialize countdown when duel is loaded
  useEffect(() => {
    if (duel) {
      // Show 0 if duel is completed, otherwise calculate remaining time
      if (duel.status === "completed") {
        setCountdown(0);
      } else {
        const initialRemainingMs = Math.max(
          0,
          duel.createdAt + duel.durationMinutes * 60_000 - Date.now()
        );
        setCountdown(Math.floor(initialRemainingMs / 1000));
      }
    }
  }, [duel, duelId]);

  // Countdown timer effect
  useEffect(() => {
    // Stop countdown if duel is completed or countdown reaches 0
    if (countdown <= 0 || (duel && duel.status === "completed")) return;

    const interval = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown, duel]);

  // Spell animation effect - hide after 3 seconds
  useEffect(() => {
    if (showSpell) {
      const timer = setTimeout(() => {
        setShowSpell(false);
        setSpellType(null);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showSpell]);

  // TODO: Fetch bets for this duel
  // useEffect(() => {
  //   if (!duelId) return;

  //   const fetchBets = async () => {
  //     try {
  //       console.log("Fetching bets for duel:");
  //       const betsData = await apiService.getDuelBets(duelId);
  //       const parsedBetsData =
  //         typeof betsData === "string" ? JSON.parse(betsData) : betsData;
  //       // console.log('Fetched bets:', parsedBetsData);
  //       setBets(Array.isArray(parsedBetsData) ? parsedBetsData : []);
  //     } catch (error) {
  //       console.error("Error fetching bets:", error);
  //       setBets([]);
  //     }
  //   };

  //   fetchBets();

  //   // Refetch bets periodically to keep crowd bar updated
  //   const interval = setInterval(fetchBets, 10000); // every 10 seconds

  //   return () => clearInterval(interval);
  // }, [duelId]);

  // Handle navigation to chart page
  const handleViewChart = () => {
    if (!duel) return;

    // Extract unique market IDs from trades to show relevant markets
    const tradedMarkets = [
      ...new Set(
        trades.map((trade) => trade.market_id).filter((id) => id !== null)
      ),
    ];

    console.log("Navigating to chart with duelId:", duelId);

    navigate("/chart", {
      state: {
        duelData: duel,
        tradesData: trades,
        tradedMarkets: tradedMarkets,
        duelId: duelId,
      },
    });
  };

  // Handle bet submission
  const handleBetSubmit = async () => {
    if (!selectedTrader || !betAmount || betLoading) return;

    try {
      setBetLoading(true);

      const betData = {
        duel_id: duelId,
        trader_type: selectedTrader, // 'challenger' or 'opponent'
        trader_user_id:
          selectedTrader === "challenger"
            ? duel.challengerUserId
            : duel.opponentUserId,
        xp_amount: parseInt(betAmount, 10),
        odds:
          selectedTrader === "challenger"
            ? Math.floor((duel.odds?.oddsA || 0) * 100) / 100
            : Math.floor((duel.odds?.oddsB || 0) * 100) / 100,
      };

      const result = await apiService.duelBet(betData);

      console.log("Bet placed successfully:");

      // Close bet modal and reset state
      setShowBetModal(false);
      setSelectedTrader(null);
      setBetAmount("");

      // Refetch bets to update crowd bar immediately
      try {
        const betsData = await apiService.getDuelBets(duelId);
        const parsedBetsData =
          typeof betsData === "string" ? JSON.parse(betsData) : betsData;
        setBets(Array.isArray(parsedBetsData) ? parsedBetsData : []);
      } catch (error) {
        console.error("Error refetching bets after bet placement:", error);
      }

      // Show success modal
      setBetResult({
        success: true,
        message: result.message || "Bet placed successfully!",
        remaining_balance: result.remaining_balance,
      });
      setShowBetResultModal(true);
    } catch (error) {
      console.error("Error placing bet:", error);

      // Show error modal
      setBetResult({
        success: false,
        message: error.error || "Failed to place bet",
      });
      setShowBetResultModal(true);
    } finally {
      setBetLoading(false);
    }
  };

  // Check for duel status and show appropriate modal
  useEffect(() => {
    if (loading) return; // Don't show modals while loading

    if (error || (!duel && !loading)) {
      setModalType("not-found");
      setShowModal(true);
    }
  }, [duel, error, loading]);

  // Refetch duel data when duel is completed
  useEffect(() => {
    if (duel && duel.status === "completed" && !duel) {
      // Refetch duel data to ensure we have the latest completion data
      (async () => {
        try {
          const duelData = await apiService.getDuel(duelId);
          const parsedDuelData =
            typeof duelData === "string" ? JSON.parse(duelData) : duelData;
          const normalizedDuel = normalizeDuel(parsedDuelData);
          setDuel(normalizedDuel);
        } catch (error) {
          console.error("Error refetching completed duel:", error);
        }
      })();
    }
  }, [duel, duelId]);

  // Fetch trade details from API if trades list is empty
  useEffect(() => {
    if (!duel || trades.length > 0 || loading) return;

    const fetchTradeDetails = async () => {
      try {
        console.log("Fetching trade details from API...");
        const data = await apiService.getTradeDetails(
          duel.challengerUserId,
          duel.opponentUserId,
          duel.id
        );
        // console.log('Trade details response:', data);

        // Process orders from both challenger and opponent
        const allOrders = [];

        if (data.challengerOrders && data.challengerOrders.length > 0) {
          data.challengerOrders.forEach((order) => {
            allOrders.push({
              ...order,
              user: duel.challengerUsername,
              side: order.side || "Trade",
              timestamp: parseHttpDate(order.created_at) || Date.now(),
              trade_id: order._id,
            });
          });
        }

        if (data.opponentOrders && data.opponentOrders.length > 0) {
          data.opponentOrders.forEach((order) => {
            allOrders.push({
              ...order,
              user: duel.opponentUsername,
              side: order.side || "Trade",
              timestamp: parseHttpDate(order.created_at) || Date.now(),
              trade_id: order._id,
            });
          });
        }

        if (allOrders.length > 0) {
          // console.log('Adding orders from API:', allOrders.length);

          // Sort orders by timestamp (newest first)
          const sortedOrders = allOrders.sort((a, b) => {
            return (b.timestamp || 0) - (a.timestamp || 0);
          });

          setTrades(sortedOrders);
        }
      } catch (error) {
        console.error("Error fetching trade details:", error);
      }
    };

    fetchTradeDetails();
  }, [duel, trades.length, loading]);

  // Show loading state
  if (loading) {
    return (
      <div className="dl-root">
        <div className="dl-main">
          <div style={{ textAlign: "center", padding: "50px" }}>
            Loading duel details...
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !duel) {
    return (
      <div className="dl-root">
        <div className="dl-top">
          <span className="dl-live">ERROR</span>
        </div>
        <div className="dl-main">
          <div style={{ textAlign: "center", padding: "50px", color: "red" }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  // Ensure duel data exists
  if (!duel) {
    return (
      <div className="dl-root">
        <div className="dl-top">
          <span className="dl-live">NO DATA</span>
        </div>
        <div className="dl-main">
          <div style={{ textAlign: "center", padding: "50px" }}>
            No duel data available
          </div>
        </div>
      </div>
    );
  }

  const remainingMs = countdown * 1000;
  const mins = Math.floor(remainingMs / 60000);
  const secs = Math.floor((remainingMs % 60000) / 1000);
  const remainingStr = `${String(mins).padStart(2, "0")}:${String(
    secs
  ).padStart(2, "0")}`;

  const challengerPnl = duel.challengerPnl;
  const opponentPnl = duel.opponentPnl;

  // Calculate crowd confidence based on bets data
  const activeBets = bets.filter((bet) => bet.status === "active");
  const challengerBetsTotal = activeBets
    .filter((bet) => bet.trader_type === "challenger")
    .reduce((total, bet) => total + (bet.xp_amount || 0), 0);
  const opponentBetsTotal = activeBets
    .filter((bet) => bet.trader_type === "opponent")
    .reduce((total, bet) => total + (bet.xp_amount || 0), 0);
  const totalBetsXP = challengerBetsTotal + opponentBetsTotal;

  // Calculate percentages, default to 50/50 if no bets
  let leftPct = 50;
  let rightPct = 50;
  if (totalBetsXP > 0) {
    leftPct = Math.round((challengerBetsTotal / totalBetsXP) * 100);
    rightPct = Math.round((opponentBetsTotal / totalBetsXP) * 100);
    // Ensure they add up to 100% (rounding can cause off-by-one)
    if (leftPct + rightPct !== 100) {
      leftPct = 100 - rightPct;
    }
  }

  return (
    <div className="dl-root">
      {/* Top Bar */}
      <div className="dl-top" onClick={() => navigate("/")}>
        <img className="dl-menu-logo" src={logo} alt="LUMOS" />
        <span className="dl-menu">LUMOS</span>
      </div>

      {/* Round + Timer Card */}
      <div className="dl-round-card-container">
        <div className="dl-round-card">
          <img className="dl-round-divider" src={lineLeft} alt="Line Left" />
          <div className="dl-round-content">
            {duel.status === "completed" ? (
              <>
                <div className="dl-round-title orange">TIME IS UP</div>
                <div className="dl-timer">RESULTS</div>
              </>
            ) : (
              <>
                <div className="dl-round-title">LIVE</div>
                <div className="dl-timer">{remainingStr}</div>
              </>
            )}
          </div>
          <img
            className="dl-round-divider"
            src={duel.status === "completed" ? lineRightCompleted : lineRight}
            alt="Line Right"
          />
        </div>

        {duel.wagerXp && duel.wagerXp > 0 && duel.status !== "completed" && (
          <div
            className="dl-stake-card"
            onClick={() => setShowStakePoolModal(true)}
            style={{ cursor: "pointer", marginTop: "10px" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img style={{ width: "12px" }} src={logo} alt="LUMOS" />
              <div className="dl-stake">STAKE POOL</div>
            </div>
            <div className="dl-stake orange">{duel.wagerXp * 2} XP</div>
          </div>
        )}
      </div>

      <div className="dl-main">
        {/* Players + VS or Results (if completed) */}
        {duel.status !== "completed" ? (
          <div className="dl-players">
            <div className="dl-player left">
              <PnlRibbon value={challengerPnl} side="left" />
              <div className="dl-player-row">
                <div className="dl-avatar left">
                  {duel.challengerAvatarUrl ? (
                    <img
                      className="dl-avatar-img"
                      src={duel.challengerAvatarUrl}
                      alt={duel.challengerUsername}
                    />
                  ) : (
                    <div className="dl-avatar-fallback">
                      {duel.challengerUsername.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="dl-meta left">
                  <div className="dl-name">{duel.challengerUsername}</div>
                  <div className="dl-role">CHALLENGER</div>
                  <div className="dl-stats-line">
                    <span className="">
                      {duel.challengerWinCount}W — {duel.opponentWinCount}L
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="dl-vs">
              <img src={vsImg} alt="VS" />
            </div>

            <div className="dl-player right">
              <PnlRibbon value={opponentPnl} side="right" />
              <div className="dl-player-row">
                <div className="dl-meta right">
                  <div className="dl-name">{duel.opponentUsername}</div>
                  <div className="dl-role">HOST</div>
                  <div className="dl-stats-line">
                    <span className="">
                      {duel.opponentWinCount}W — {duel.challengerWinCount}L
                    </span>
                  </div>
                </div>
                <div className="dl-avatar right">
                  {duel.opponentAvatarUrl ? (
                    <img
                      className="dl-avatar-img"
                      src={duel.opponentAvatarUrl}
                      alt={duel.opponentUsername}
                    />
                  ) : (
                    <div className="dl-avatar-fallback">
                      {duel.opponentUsername.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="dl-players" style={{ position: "relative" }}>
            {(() => {
              // Determine winner based on winner_user_id (fallback to pnl if missing)
              const resolvedWinnerId =
                duel.winnerUserId ||
                (challengerPnl >= opponentPnl
                  ? duel.challengerUserId
                  : duel.opponentUserId);

              const challengerWon = resolvedWinnerId === duel.challengerUserId;
              const opponentWon = resolvedWinnerId === duel.opponentUserId;

              return (
                <>
                  <div
                    className="dl-player left"
                    style={{ position: "relative", top: 10 }}
                  >
                    <img
                      src={challengerWon ? victoryImg : defeatImg}
                      alt={challengerWon ? "Victory" : "Defeat"}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: 220,
                        pointerEvents: "none",
                        zIndex: 10,
                      }}
                    />
                    <div
                      className="dl-player-row"
                      style={{
                        position: "absolute",
                        transform: "rotate(7deg)",
                        height: "100px",
                        width: "100%",
                      }}
                    >
                      <div
                        className="dl-meta"
                        style={{
                          position: "absolute",
                          left: "30%",
                          bottom: "-15%",
                          transform: "rotate(-3deg)",
                        }}
                      >
                        <div className="dl-name">{duel.challengerUsername}</div>
                        <div className="dl-role">CHALLENGER</div>
                      </div>
                      <div
                        className="dl-avatar"
                        style={{
                          width: 100,
                          height: 100,
                          marginRight: 15,
                          position: "absolute",
                          right: 0,
                        }}
                      >
                        {duel.challengerAvatarUrl ? (
                          <img
                            className="dl-avatar-img"
                            src={duel.challengerAvatarUrl}
                            alt={duel.challengerUsername}
                          />
                        ) : (
                          <div className="dl-avatar-fallback">
                            {duel.challengerUsername.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <PnlRibbon
                          value={challengerPnl}
                          side="left"
                          style={{
                            position: "relative",
                            transform: "rotate(-5deg)",
                            left: -20,
                          }}
                          spanStyle={{
                            transform: "rotate(3deg)",
                            width: "100%",
                            fontSize: "10px",
                            fontWeight: "900",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className="dl-vs"
                    style={{
                      width: 160,
                      top: "55%",
                    }}
                  >
                    <img src={vsImg} alt="VS" />
                  </div>

                  <div
                    className="dl-player left"
                    style={{ position: "relative", top: "55%" }}
                  >
                    <img
                      src={opponentWon ? victoryImg : defeatImg}
                      alt={opponentWon ? "Victory" : "Defeat"}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: 220,
                        pointerEvents: "none",
                        zIndex: 10,
                      }}
                    />
                    <div
                      className="dl-player-row"
                      style={{
                        position: "absolute",
                        transform: "rotate(7deg)",
                        height: "100px",
                        width: "100%",
                      }}
                    >
                      <div
                        className="dl-meta"
                        style={{
                          position: "absolute",
                          left: "30%",
                          bottom: "-15%",
                          transform: "rotate(-3deg)",
                        }}
                      >
                        <div className="dl-name">{duel.opponentUsername}</div>
                        <div className="dl-role">HOST</div>
                      </div>
                      <div
                        className="dl-avatar"
                        style={{
                          width: 100,
                          height: 100,
                          marginRight: 15,
                          position: "absolute",
                          right: 0,
                        }}
                      >
                        {duel.opponentAvatarUrl ? (
                          <img
                            className="dl-avatar-img"
                            src={duel.opponentAvatarUrl}
                            alt={duel.opponentUsername}
                          />
                        ) : (
                          <div className="dl-avatar-fallback">
                            {duel.opponentUsername.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <PnlRibbon
                          value={opponentPnl}
                          side="right"
                          style={{
                            position: "relative",
                            transform: "rotate(-5deg)",
                            left: -20,
                          }}
                          spanStyle={{
                            transform: "rotate(3deg)",
                            width: "100%",
                            fontSize: "10px",
                            fontWeight: "900",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Trades List */}
        <div className="dl-trades">
          <div className="dl-trades-head">
            <div className="dl-trades-title">TRADES</div>
            {/* <button className="dl-see-all">SEE ALL</button> */}
            {process.env.NODE_ENV === "development" && (
              <div
                style={{ fontSize: "10px", color: "#666", marginLeft: "10px" }}
              >
                Trades: {trades.length}
              </div>
            )}
          </div>
          <ul className="dl-trades-list">
            {trades.length > 0 ? (
              trades.map((trade, index) => {
                // Use the pre-calculated user and side information
                const user = trade.user || "Unknown";
                const side = trade.side || "Unknown";
                const marketName =
                  marketSymbolMap[trade.market_id] ||
                  `Market ${trade.market_id}`;
                let tradeAvatar = null;

                if (user === duel.challengerUsername) {
                  tradeAvatar = duel.challengerAvatarUrl;
                } else {
                  tradeAvatar = duel.opponentAvatarUrl;
                }

                // Format: "{user} Casted {ticker} {side}"
                const tradeText = `Casted ${marketName} ${side}`;

                // Format timestamp (handle both milliseconds and seconds)
                const timestamp =
                  trade.timestamp > 10000000000
                    ? trade.timestamp
                    : trade.timestamp * 1000;
                const tradeTime = new Date(timestamp).toLocaleTimeString({
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                });

                return (
                  <li
                    key={trade.trade_id || index}
                    className={`dl-trade ${index >= 3 ? "dim" : ""}`}
                  >
                    {tradeAvatar ? (
                      <img
                        className="dl-trade-ava"
                        src={tradeAvatar}
                        alt="Trade"
                      />
                    ) : (
                      <div className="dl-trade-ava">
                        {duel.opponentUsername.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="dl-trade-text">{tradeText}</div>
                    <div className="dl-trade-time">{tradeTime}</div>
                  </li>
                );
              })
            ) : (
              <>
                <li className="dl-trade">
                  <div className="dl-trade-ava" />
                  <div className="dl-trade-text">No recent trades</div>
                  <div className="dl-trade-time">--:--</div>
                </li>
              </>
            )}
          </ul>
        </div>

        {/* TODO: Crowd Bar */}
        {/* <div className="dl-crowd">
          <div className="dl-crowd-labels">
            <span className="left">
              {duel.challengerUsername} — {leftPct}%
            </span>
            <span className="right">
              {duel.opponentUsername} — {rightPct}%
            </span>
          </div>
          <div className="dl-crowd-bar">
            <div className="left" style={{ width: `${leftPct}%` }} />
            <div className="right" style={{ width: `${rightPct}%` }} />
            <img
              className="handle"
              src={handleImg}
              alt="Handle"
              style={{ left: `${leftPct}%` }}
            />
          </div>
        </div> */}

        {/* Actions */}
        {duel.status === "completed" ? (
          <div className="dl-actions-full">
            <button
              className="dl-bet primary"
              onClick={handleViewChart}
              style={{
                backgroundColor: "#424243",
                color: "#FFFFFF",
                minWidth: "100%",
                borderRadius: "2px",
                fontSize: "18px",
              }}
            >
              <img
                src={chartIconSvg}
                alt="Chart"
                style={{ width: 14, height: 14, marginRight: 8 }}
              />
              CHART
            </button>
          </div>
        ) : (
          <div className="dl-actions">
            <img
              className="dl-bet primary"
              src={betIcon}
              alt="Bet"
              onClick={() => {}}
              style={{ cursor: "pointer" }}
              disabled
            />
            <img
              className="dl-bet secondary"
              src={chartIcon}
              alt="View Chart"
              onClick={handleViewChart}
              style={{ cursor: "pointer" }}
            />
          </div>
        )}
      </div>

      {/* Spell Animation */}
      {showSpell && spellType && (
        <div className="dl-spell-overlay">
          <img
            src={
              {
                fire_1,
                fire_2,
                fire_3,
                electric_1,
                electric_2,
                electric_3,
              }[spellType] || fire_1
            }
            alt={spellType}
          />
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="dl-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="dl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dl-modal-content">
              <h2 className="dl-modal-title">
                {modalType === "completed"
                  ? "Duel Completed"
                  : "Duel Not Found"}
              </h2>
              <p className="dl-modal-message">
                {modalType === "completed"
                  ? "This duel has finished. Check the results!"
                  : "The requested duel could not be found or an error occurred."}
              </p>
              <button
                className="dl-modal-close"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stake Pool Modal */}
      {showStakePoolModal && (
        <div
          className="dl-modal-overlay"
          onClick={() => setShowStakePoolModal(false)}
        >
          <div className="dl-modal stake" onClick={(e) => e.stopPropagation()}>
            <div className="dl-modal-content">
              <div className="dl-stake-header">
                <div className="dl-stake-title">STAKE POOL</div>
              </div>
              <div className="dl-stake-body">
                <div className="dl-stake-rows">
                  <div className="dl-stake-row">
                    <div className="dl-stake-user">
                      {/* avatars already on page, keep simple initials here */}
                      <div className="dl-stake-ava">
                        <img style={{ width: "100%" }} src={duel.challengerAvatarUrl} alt={duel.challengerUsername} />
                      </div>
                      <div className="dl-stake-name">
                        {duel.challengerUsername}
                      </div>
                    </div>
                    <div className="dl-stake-xp">{duel.wagerXp} XP</div>
                  </div>
                  <div className="dl-stake-row">
                    <div className="dl-stake-user">
                      <div className="dl-stake-ava">
                        <img style={{ width: "100%" }} src={duel.opponentAvatarUrl} alt={duel.opponentUsername} />
                      </div>
                      <div className="dl-stake-name">
                        {duel.opponentUsername}
                      </div>
                    </div>
                    <div className="dl-stake-xp">{duel.wagerXp} XP</div>
                  </div>
                </div>
                <div className="dl-stake-total">
                <img className="dl-menu-logo" src={logo} alt="LUMOS" />
                  <div className="dl-stake-winner">WINNER TAKES ALL</div>
                  <div className="dl-stake-total-xp">{Math.floor(duel.wagerXp * 2)}<br/>XP</div>
                </div>
              </div>
              <button
                className="dl-modal-x"
                onClick={() => setShowStakePoolModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Betting Modal */}
      {showBetModal && (
        <div
          className="dl-modal-overlay"
          onClick={() => setShowBetModal(false)}
        >
          <div className="dl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dl-modal-content">
              <h2 className="dl-modal-title">Place Your Bet</h2>

              {/* Trader Selection */}
              <div style={{ marginBottom: "20px" }}>
                <h3
                  style={{
                    fontSize: "16px",
                    marginBottom: "12px",
                    color: "#fff",
                  }}
                >
                  Select Trader
                </h3>
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                  }}
                >
                  {/* Challenger Option */}
                  <div
                    onClick={() => setSelectedTrader("challenger")}
                    style={{
                      padding: "16px",
                      border: `2px solid ${
                        selectedTrader === "challenger"
                          ? "#39ff6e"
                          : "rgba(255,255,255,0.2)"
                      }`,
                      borderRadius: "12px",
                      background:
                        selectedTrader === "challenger"
                          ? "rgba(57, 255, 110, 0.1)"
                          : "rgba(255,255,255,0.05)",
                      cursor: "pointer",
                      flex: 1,
                      textAlign: "center",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "900",
                        marginBottom: "4px",
                        wordBreak: "break-all",
                      }}
                    >
                      {duel.challengerUsername}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        opacity: 0.8,
                        marginBottom: "8px",
                      }}
                    >
                      Challenger
                    </div>
                    {duel.odds && duel.odds.oddsA !== null && (
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#39ff6e",
                        }}
                      >
                        {Math.floor(duel.odds.oddsA * 100) / 100}x
                      </div>
                    )}
                  </div>

                  {/* Opponent Option */}
                  <div
                    onClick={() => setSelectedTrader("opponent")}
                    style={{
                      padding: "16px",
                      border: `2px solid ${
                        selectedTrader === "opponent"
                          ? "#39ff6e"
                          : "rgba(255,255,255,0.2)"
                      }`,
                      borderRadius: "12px",
                      background:
                        selectedTrader === "opponent"
                          ? "rgba(57, 255, 110, 0.1)"
                          : "rgba(255,255,255,0.05)",
                      cursor: "pointer",
                      flex: 1,
                      textAlign: "center",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "900",
                        marginBottom: "4px",
                        wordBreak: "break-all",
                      }}
                    >
                      {duel.opponentUsername}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        opacity: 0.8,
                        marginBottom: "8px",
                      }}
                    >
                      Host
                    </div>
                    {duel.odds && duel.odds.oddsB !== null && (
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#39ff6e",
                        }}
                      >
                        {Math.floor(duel.odds.oddsB * 100) / 100}x
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Bet Amount Input */}
              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "16px",
                    marginBottom: "8px",
                    color: "#fff",
                  }}
                >
                  Bet Amount (XP)
                </label>
                <input
                  type="number"
                  value={betAmount}
                  onChange={(e) => setBetAmount(e.target.value)}
                  placeholder="Enter amount..."
                  style={{
                    width: "90%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: "rgba(255,255,255,0.05)",
                    color: "#fff",
                    fontSize: "16px",
                    textAlign: "center",
                  }}
                  min="1"
                  step="1"
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  className="dl-modal-close"
                  onClick={() => setShowBetModal(false)}
                  style={{
                    flex: 1,
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                  }}
                >
                  Cancel
                </button>
                <button
                  className="dl-modal-close"
                  onClick={handleBetSubmit}
                  disabled={!selectedTrader || !betAmount || betLoading}
                  style={{
                    flex: 1,
                    opacity:
                      !selectedTrader || !betAmount || betLoading ? 0.5 : 1,
                    cursor:
                      !selectedTrader || !betAmount || betLoading
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {betLoading ? "Placing Bet..." : "Place Bet"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bet Result Modal */}
      {showBetResultModal && betResult && (
        <div
          className="dl-modal-overlay"
          onClick={() => setShowBetResultModal(false)}
        >
          <div className="dl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dl-modal-content">
              <h2
                className="dl-modal-title"
                style={{
                  color: betResult.success ? "#39ff6e" : "#ff5b5b",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {betResult.success ? "Bet Successful!" : "Bet Failed"}
              </h2>

              <p
                className="dl-modal-message"
                style={{
                  color: betResult.success
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(255, 255, 255, 0.8)",
                  fontSize: "16px",
                  lineHeight: "1.5",
                  marginBottom:
                    betResult.success &&
                    betResult.remaining_balance !== undefined
                      ? "16px"
                      : "24px",
                }}
              >
                {betResult.message}
              </p>

              {betResult.success &&
                betResult.remaining_balance !== undefined && (
                  <div
                    style={{
                      background: "rgba(57, 255, 110, 0.1)",
                      border: "1px solid rgba(57, 255, 110, 0.3)",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      marginBottom: "24px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "14px",
                        color: "rgba(255, 255, 255, 0.7)",
                        marginBottom: "4px",
                      }}
                    >
                      Remaining XP Balance
                    </div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        color: "#39ff6e",
                      }}
                    >
                      {betResult.remaining_balance} XP
                    </div>
                  </div>
                )}

              <button
                className="dl-modal-close"
                onClick={() => setShowBetResultModal(false)}
                style={{
                  background: betResult.success
                    ? "linear-gradient(135deg, #e8f8ec 0%, #e3ede7 100%)"
                    : "linear-gradient(135deg, #ffeaea 0%, #ffe5e5 100%)",
                  color: betResult.success ? "#000" : "#333",
                  width: "100%",
                }}
              >
                {betResult.success ? "Continue Watching" : "Try Again"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
